import { MakeLogicType, actions, kea, listeners, path, reducers } from 'kea'
import posthog from 'posthog-js'

import api from 'lib/api'
import { tryShowMCPHint } from 'lib/components/MCPHint/mcpHintLogic'
import { SetupTaskId, globalSetupLogic } from 'lib/components/ProductSetup'
import { TaxonomicFilterGroupType } from 'lib/components/TaxonomicFilter/types'
import { BehavioralFilterKey } from 'scenes/cohorts/CohortFilters/types'
import { createCohortFormData } from 'scenes/cohorts/cohortUtils'

import { ErrorTrackingIssue } from '~/queries/schema/schema-general'
import { BehavioralEventType, CohortType, FilterLogicalOperator, PropertyFilterType, PropertyOperator } from '~/types'

import type {
    ErrorTrackingIssueAssignee,
    ErrorTrackingIssueStatus,
} from '../../../../../frontend/src/queries/schema/schema-general'
import { pendingFingerprintIssueStateUpdateLogic } from '../../logics/pendingFingerprintIssueStateUpdateLogic'

const pendingUpdateActions = ():
    | NonNullable<ReturnType<typeof pendingFingerprintIssueStateUpdateLogic.findMounted>>['asyncActions']
    | undefined => pendingFingerprintIssueStateUpdateLogic.findMounted()?.asyncActions

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issueActionsLogicValues {
    needsReload: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issueActionsLogicActions {
    activateIssues: (ids: string[]) => {
        ids: string[]
    }
    assignIssues: (
        ids: string[],
        assignee: ErrorTrackingIssue['assignee']
    ) => {
        assignee: ErrorTrackingIssueAssignee | null
        ids: string[]
    }
    clearNeedsReload: () => {
        value: true
    }
    createIssueCohort: (
        id: string,
        name: string,
        description: string
    ) => {
        description: string
        id: string
        name: string
    }
    mergeIssues: (ids: string[]) => {
        ids: string[]
    }
    mutationFailure: (
        mutationName: string,
        error: unknown
    ) => {
        error: unknown
        mutationName: string
    }
    mutationSuccess: (mutationName: string) => {
        mutationName: string
    }
    resolveIssues: (ids: string[]) => {
        ids: string[]
    }
    splitIssue: (
        id: ErrorTrackingIssue['id'],
        fingerprints: {
            description?: string
            fingerprint: string
            name?: string
        }[]
    ) => {
        fingerprints: {
            description?: string | undefined
            fingerprint: string
            name?: string | undefined
        }[]
        id: string
    }
    splitIssueSuccess: (newIssueIds: string[]) => {
        newIssueIds: string[]
    }
    suppressIssues: (ids: string[]) => {
        ids: string[]
    }
    updateIssueAssignee: (
        id: string,
        assignee: ErrorTrackingIssue['assignee']
    ) => {
        assignee: ErrorTrackingIssueAssignee | null
        id: string
    }
    updateIssueDescription: (
        id: string,
        description: string
    ) => {
        description: string
        id: string
    }
    updateIssueName: (
        id: string,
        name: string
    ) => {
        id: string
        name: string
    }
    updateIssueStatus: (
        id: string,
        status: ErrorTrackingIssue['status']
    ) => {
        id: string
        status: ErrorTrackingIssueStatus
    }
}

export type issueActionsLogicType = MakeLogicType<issueActionsLogicValues, issueActionsLogicActions>

export const issueActionsLogic = kea<issueActionsLogicType>([
    path(['products', 'error_tracking', 'components', 'IssueActions', 'issueActionsLogic']),

    actions({
        mergeIssues: (ids: string[]) => ({ ids }),
        splitIssue: (
            id: ErrorTrackingIssue['id'],
            fingerprints: { fingerprint: string; name?: string; description?: string }[]
        ) => ({
            id,
            fingerprints,
        }),
        resolveIssues: (ids: string[]) => ({ ids }),
        suppressIssues: (ids: string[]) => ({ ids }),
        activateIssues: (ids: string[]) => ({ ids }),
        assignIssues: (ids: string[], assignee: ErrorTrackingIssue['assignee']) => ({ ids, assignee }),

        updateIssueAssignee: (id: string, assignee: ErrorTrackingIssue['assignee']) => ({ id, assignee }),
        updateIssueStatus: (id: string, status: ErrorTrackingIssue['status']) => ({ id, status }),
        updateIssueName: (id: string, name: string) => ({ id, name }),
        updateIssueDescription: (id: string, description: string) => ({ id, description }),
        createIssueCohort: (id: string, name: string, description: string) => ({ id, name, description }),

        splitIssueSuccess: (newIssueIds: string[]) => ({ newIssueIds }),
        mutationSuccess: (mutationName: string) => ({ mutationName }),
        mutationFailure: (mutationName: string, error: unknown) => ({ mutationName, error }),
        clearNeedsReload: true,
    }),

    reducers({
        needsReload: [
            false,
            {
                mutationSuccess: () => true,
                clearNeedsReload: () => false,
            },
        ],
    }),

    listeners(({ actions }) => {
        async function runMutation(
            mutationName: string,
            cb: () => Promise<void>,
            onSuccess?: () => Promise<void>
        ): Promise<void> {
            try {
                await cb()
                if (onSuccess) {
                    await onSuccess()
                }
                actions.mutationSuccess(mutationName)
            } catch (e: unknown) {
                actions.mutationFailure(mutationName, e)
            }
        }
        return {
            mergeIssues: async ({ ids }) => {
                const [firstId, ...otherIds] = ids
                if (firstId && otherIds.length > 0) {
                    await runMutation(
                        'mergeIssues',
                        async () => {
                            posthog.capture('error_tracking_issue_merged', { primary: firstId })
                            await api.errorTracking.mergeInto(firstId, otherIds)
                        },
                        async () => pendingUpdateActions()?.captureMergePendingUpdates(firstId, otherIds)
                    )
                }
            },
            splitIssue: async ({ id, fingerprints }) => {
                await runMutation('splitIssues', async () => {
                    posthog.capture('error_tracking_issue_split', { issueId: id })
                    const response = await api.errorTracking.split(id, fingerprints)
                    actions.splitIssueSuccess(response.new_issue_ids)
                })
            },
            resolveIssues: async ({ ids }) => {
                await runMutation(
                    'resolveIssues',
                    async () => {
                        posthog.capture('error_tracking_issue_bulk_resolve')
                        await api.errorTracking.bulkMarkStatus(ids, 'resolved')
                    },
                    async () => pendingUpdateActions()?.capturePendingUpdatesForIssues(ids, { status: 'resolved' })
                )

                globalSetupLogic.findMounted()?.actions.markTaskAsCompleted(SetupTaskId.ResolveFirstError)
            },
            suppressIssues: async ({ ids }) => {
                await runMutation(
                    'suppressIssues',
                    async () => {
                        posthog.capture('error_tracking_issue_bulk_suppress')
                        await api.errorTracking.bulkMarkStatus(ids, 'suppressed')
                    },
                    async () => pendingUpdateActions()?.capturePendingUpdatesForIssues(ids, { status: 'suppressed' })
                )
            },
            activateIssues: async ({ ids }) => {
                await runMutation(
                    'activateIssues',
                    async () => {
                        posthog.capture('error_tracking_issue_bulk_activate')
                        await api.errorTracking.bulkMarkStatus(ids, 'active')
                    },
                    async () => pendingUpdateActions()?.capturePendingUpdatesForIssues(ids, { status: 'active' })
                )
            },
            assignIssues: async ({ ids, assignee }) => {
                await runMutation(
                    'assignIssues',
                    async () => {
                        posthog.capture('error_tracking_issue_bulk_assign')
                        await api.errorTracking.bulkAssign(ids, assignee)
                    },
                    async () => pendingUpdateActions()?.capturePendingUpdatesForIssues(ids, { assignee })
                )
            },
            updateIssueAssignee: async ({ id, assignee }) => {
                await runMutation(
                    'updateIssueAssignee',
                    async () => {
                        posthog.capture('error_tracking_issue_update_assignee')
                        await api.errorTracking.assignIssue(id, assignee)
                    },
                    async () => pendingUpdateActions()?.capturePendingUpdatesForIssues([id], { assignee })
                )
            },
            updateIssueStatus: async ({ id, status }) => {
                await runMutation(
                    'updateIssueStatus',
                    async () => {
                        posthog.capture('error_tracking_issue_update_status', {
                            status,
                            issue_id: id,
                            source: 'issue_actions',
                        })
                        await api.errorTracking.updateIssue(id, { status })
                    },
                    async () => pendingUpdateActions()?.capturePendingUpdatesForIssues([id], { status })
                )
            },
            updateIssueName: async ({ id, name }) => {
                await runMutation(
                    'updateIssueName',
                    async () => {
                        posthog.capture('error_tracking_issue_update_name')
                        await api.errorTracking.updateIssue(id, { name })
                    },
                    async () => pendingUpdateActions()?.capturePendingUpdatesForIssues([id], { name })
                )
            },
            updateIssueDescription: async ({ id, description }) => {
                await runMutation(
                    'updateIssueDescription',
                    async () => {
                        posthog.capture('error_tracking_issue_update_description')
                        await api.errorTracking.updateIssue(id, { description })
                    },
                    async () => pendingUpdateActions()?.capturePendingUpdatesForIssues([id], { description })
                )
            },
            createIssueCohort: async ({ id, name, description }) => {
                await runMutation('createIssueCohort', async () => {
                    let cohortParams = createCohortParams(name, description, id)
                    let formData = createCohortFormData(cohortParams)
                    let cohort = await api.cohorts.create(formData as Partial<CohortType>)
                    posthog.capture('error_tracking_issue_create_cohort', {
                        issueId: id,
                        cohortId: cohort.id,
                    })
                    await api.errorTracking.assignCohort(id, cohort.id)
                })
            },
            mutationSuccess: ({ mutationName }) => {
                if (mutationName === 'updateIssueAssignee' || mutationName === 'assignIssues') {
                    tryShowMCPHint('error_tracking.assign')
                }
            },
        }
    }),
])

function createCohortParams(name: string, description: string, issueId: string): CohortType {
    return {
        id: 'new',
        name,
        description,
        groups: [],
        filters: {
            properties: {
                type: FilterLogicalOperator.Or,
                values: [
                    {
                        type: FilterLogicalOperator.Or,
                        values: [
                            {
                                key: '$exception',
                                type: BehavioralFilterKey.Behavioral,
                                value: BehavioralEventType.PerformEvent,
                                negation: false,
                                event_type: TaxonomicFilterGroupType.Events,
                                event_filters: [
                                    {
                                        key: '$exception_issue_id',
                                        type: PropertyFilterType.Event,
                                        value: [issueId],
                                        operator: PropertyOperator.Exact,
                                    },
                                ],
                                explicit_datetime: '-30d',
                            },
                        ],
                    },
                ],
            },
        },
    }
}
